import { useCallback, useEffect, useRef } from 'react';
import { WS_EVENTS } from '@twinden/shared';
import { getDefaultStore } from 'jotai';
import { useToast } from '@chakra-ui/react';
import { t } from '../i18n';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  familiesAtom,
  currentFamilyAtom,
  familiesLoadingAtom,
  familiesErrorAtom,
  currentFamilyIdAtom,
  currentFamilyRoleAtom,
  isCurrentFamilyAdminAtom,
  isCurrentFamilyRootAtom,
  hasFamiliesAtom,
  setFamiliesAtom,
  setCurrentFamilyAtom,
  currentUserAtom,
  updateFamilyAtom,
  removeFamilyAtom,
  type FamilyListItem,
  type FamilyWithRole,
} from '../store';
import { api, ApiError } from '../lib/api';
import { noteFamilyList, noteScopeMembers } from '../lib/e2ee/session';
import { setFamilyPicture } from '../lib/pictureSetting';
import { loadPictureKeys } from '../lib/e2ee/pictures';
import { useWebSocket } from './useWebSocket';
import {
  forgetKnownFamily,
  readKnownFamilies,
  writeKnownFamilies,
} from '../lib/familyDeletedNote';
import { applyFamilyDeleted } from './useFamilyDeleted';

// Family state hook (identity spec §7.3). Reads/writes the family atoms and
// talks to the family-management endpoints.
export function useFamily() {
  const families = useAtomValue(familiesAtom);
  const currentFamily = useAtomValue(currentFamilyAtom);
  const [currentFamilyId, setCurrentFamilyId] = useAtom(currentFamilyIdAtom);
  const [loading, setLoading] = useAtom(familiesLoadingAtom);
  const [error, setError] = useAtom(familiesErrorAtom);
  const currentRole = useAtomValue(currentFamilyRoleAtom);
  const isCurrentFamilyAdmin = useAtomValue(isCurrentFamilyAdminAtom);
  const isCurrentFamilyRoot = useAtomValue(isCurrentFamilyRootAtom);
  const hasFamilies = useAtomValue(hasFamiliesAtom);

  const setFamilies = useSetAtom(setFamiliesAtom);
  const setCurrentFamily = useSetAtom(setCurrentFamilyAtom);
  const updateFamilyAtomValue = useSetAtom(updateFamilyAtom);
  const removeFamilyAtomValue = useSetAtom(removeFamilyAtom);
  // useToast returns a new function each render; keep loadFamilies stable.
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const loadFamilies = useCallback(async (): Promise<FamilyListItem[]> => {
    setLoading(true);
    try {
      const response = await api.getFamilies();
      const store = getDefaultStore();
      const me = store.get(currentUserAtom)?.id;
      const known = me ? readKnownFamilies(me) : null;
      setFamilies(response.data);
      if (me) writeKnownFamilies(me, response.data);
      // On a device from before M73, the first family list after the update
      // names the families that may have a first look (D304).
      void noteFamilyList(response.data.map((family) => family.id));
      // Gone while this device was away (D191): forget it the same way as
      // when the socket says so. Who did it isn't known here.
      const gone = (known ?? []).filter(
        (family) => !response.data.some((f) => f.id === family.id)
      );
      for (const family of gone) {
        await applyFamilyDeleted(
          { familyId: family.id, familyName: family.name },
          store,
          (title) => toastRef.current({ title, status: 'info', duration: 8000 })
        );
      }
      return gone.length > 0 ? store.get(familiesAtom) : response.data;
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : t('account.family.loadFamiliesFailed')
      );
      return [];
    } finally {
      setLoading(false);
    }
  }, [setFamilies, setError, setLoading]);

  const loadFamily = useCallback(
    async (familyId: string): Promise<FamilyWithRole | null> => {
      setLoading(true);
      try {
        const response = await api.getFamily(familyId);
        setCurrentFamily(response.data);
        // Who is in the family as the app shows it: the keys check reads
        // this, not the owners a keys answer lists (M73, D301).
        void noteScopeMembers([
          {
            kind: 'family',
            id: familyId,
            userIds: response.data.members.map((member) => member.userId),
          },
        ]);
        return response.data;
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : t('account.family.loadFailed')
        );
        return null;
      } finally {
        setLoading(false);
      }
    },
    [setCurrentFamily, setError, setLoading]
  );

  const updateFamily = useCallback(
    async (
      familyId: string,
      data: {
        name?: string;
        description?: string | null;
        /** A picture as a data: URL, sealed here first (D291); null removes it. */
        picture?: string | null;
      }
    ) => {
      const { picture, ...rest } = data;
      const response =
        picture !== undefined
          ? await setFamilyPicture(familyId, picture)
          : await api.updateFamily(familyId, rest);
      updateFamilyAtomValue({
        id: familyId,
        updates: response.data as Partial<FamilyListItem>,
      });
      return response.data;
    },
    [updateFamilyAtomValue]
  );

  const updateMember = useCallback(
    (
      familyId: string,
      userId: string,
      data: { role?: 'root' | 'admin' | 'member'; nickname?: string | null }
    ) => api.updateFamilyMember(familyId, userId, data),
    []
  );

  const removeMember = useCallback(
    (familyId: string, userId: string) =>
      api.removeFamilyMember(familyId, userId),
    []
  );

  // Forget a family locally (after leaving it or being removed)
  const forgetFamily = useCallback(
    (familyId: string) => {
      removeFamilyAtomValue(familyId);
      // Left on purpose: nothing to catch up on later (D191).
      const me = getDefaultStore().get(currentUserAtom)?.id;
      if (me) forgetKnownFamily(me, familyId);
    },
    [removeFamilyAtomValue]
  );

  return {
    families,
    currentFamily,
    currentFamilyId,
    currentRole,
    isCurrentFamilyAdmin,
    isCurrentFamilyRoot,
    hasFamilies,
    loading,
    error,
    setCurrentFamilyId,
    loadFamilies,
    loadFamily,
    updateFamily,
    updateMember,
    removeMember,
    forgetFamily,
  };
}

/**
 * Keeps the picture keys of every family I'm in (D292), so faces open on
 * whichever family's screen they're drawn: all of them when the list of
 * families first loads or changes (signing in, joining), the family I
 * switch to again, and a family again when someone joins or leaves it or
 * changes their picture. A family key arriving is handled where the keys
 * live (lib/e2ee/pictures.ts). Mounted once, in the app shell.
 */
export function usePictureKeys() {
  const families = useAtomValue(familiesAtom);
  const currentFamilyId = useAtomValue(currentFamilyIdAtom);
  const userId = useAtomValue(currentUserAtom)?.id;
  const { onEvent } = useWebSocket();
  const familyIds = families
    .map((family) => family.id)
    .sort()
    .join(',');

  useEffect(() => {
    if (!userId || !familyIds) return;
    for (const id of familyIds.split(',')) void loadPictureKeys(id);
  }, [userId, familyIds]);

  useEffect(() => {
    if (userId && currentFamilyId) void loadPictureKeys(currentFamilyId);
  }, [userId, currentFamilyId]);

  useEffect(
    () =>
      onEvent(({ event, payload }) => {
        const { familyId } = (payload ?? {}) as { familyId?: string };
        if (
          (event === WS_EVENTS.FAMILY_MEMBER_JOINED ||
            event === WS_EVENTS.FAMILY_MEMBER_LEFT) &&
          familyId
        ) {
          void loadPictureKeys(familyId, { fresh: true });
        } else if (event === WS_EVENTS.USER_UPDATED && familyIds) {
          // A new picture comes with new keys, in every family we share.
          for (const id of familyIds.split(','))
            void loadPictureKeys(id, { fresh: true });
        }
      }),
    [onEvent, userId, familyIds]
  );
}
